// hook
import { useEditGroup } from "containers/EditGroup/useEditGroup";
import { useRoleManagement } from "containers/RoleManagement/useRoleManagement";

// types
import { FieldErrors, GroupDetails, GroupPermissionItem, PermissionGroup, RoleGroupItem } from "containers/NewGroup/useNewGroup";

// components
import { Button } from "antd";
import getIcon from "utils/getIcon";
import ButtonComponent from "components/atoms/Button/Button";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import RoleGroup from "components/molecules/RoleGroup/RoleGroup";
import NewGroupDetails from "components/organisms/NewGroup/NewGroupDetails";
import { Head } from "components/molecules/Head/Head";
import Spinner from "components/atoms/Spinner/Spinner";

// styles
import styles from "containers/EditGroup/editGroupPage.module.css";

// constants
import { EditGroup as Constants, ViewMode, PageTitles } from "constants/global";

type ComponentProps = {
	initialValues: {
		name: string;
		description: string;
		selectedRoles: string[];
	};
	mode: string;
	groupDetails: GroupDetails;
	isCreateGroupButtonEnabled?: boolean;
	permissionList: PermissionGroup[];
	selectedRoles?: Array<string>;
	isDetailsUpdated: boolean;
	fieldErrors?: FieldErrors;
	isGroupActive: boolean;
	loading: boolean;
	btnLoading: boolean;
	toggleActivation: () => void;
	validateFields: (arg: boolean) => void;
	handleMode: (v: string) => void;
	handleOnPermissionSelect: (value: string) => void;
	handleGoBack: () => void;
	proceedToCreateGroup?: () => void;
	handleGroupDetailsChange: ({ id, value }: RoleGroupItem) => void;
	handleDeactivateGroup: () => void;
	submitChanges: () => void;
};

const EditGroupPage = () => {
	const componentProps: ComponentProps = useEditGroup();
	const { createGroupAccess } = useRoleManagement();

	const {
		initialValues,
		isDetailsUpdated,
		mode,
		groupDetails,
		permissionList,
		selectedRoles,
		fieldErrors,
		isGroupActive,
		loading,
		btnLoading,
		toggleActivation,
		validateFields,
		submitChanges,
		handleGoBack,
		handleMode,
		handleDeactivateGroup,
		handleOnPermissionSelect,
		handleGroupDetailsChange
	} = componentProps;
	const readOnly = mode === ViewMode.READ;

	if (loading) {
		return <Spinner />;
	}

	return (
		<div className={styles?.root}>
			{btnLoading && <Spinner type="transparent" />}
			<Head>{PageTitles.EDIT_GROUP}</Head>
			<PageTitle showBackIcon={true} title={Constants.TITLE} subTitle={Constants.SUB_TITLE} goBack={handleGoBack} />
			<div className={styles?.main_wrapper}>
				<div className={styles?.content}>
					<NewGroupDetails
						validateFields={validateFields}
						fieldErrors={fieldErrors}
						mode={mode}
						initialValues={initialValues}
						groupDetails={groupDetails}
						handleGroupDetailsChange={handleGroupDetailsChange}
					/>
				</div>
				<div className={styles?.admin_rights_group_wrapper}>
					<div className={styles?.rights_group}>
						{permissionList?.map(({ title, values, disable }: { title?: string; values?: Array<GroupPermissionItem>; disable?: boolean }, idx: number) => {
							const props = {
								checkedRoles: selectedRoles,
								title,
								values
							};
							return readOnly ? (
								<RoleGroup {...props} readonly key={`${title}-${idx}`} />
							) : (
								<RoleGroup disabled={disable} {...props} onClick={handleOnPermissionSelect} key={`${title}-${idx}`} />
							);
						})}
					</div>
				</div>
				<div className={styles?.edit_buttons_group_wrapper}>
					<div className={styles?.buttons_wrapper}>
						{readOnly && isGroupActive && (
							<Button type="primary" danger className={styles?.button} onClick={() => handleDeactivateGroup()}>
								<span className={styles?.button_label}>{Constants.DEACTIVATE_GROUP_LABEL}</span>
							</Button>
						)}
						{readOnly && !isGroupActive && (
							<Button className={styles?.reactivate_button} onClick={() => toggleActivation()}>
								<span className={styles?.button_label}>{Constants.REACTIVATE_GROUP_LABEL}</span>
							</Button>
						)}
						{mode === ViewMode.EDIT && (
							<ButtonComponent
								onClick={submitChanges}
								type={isDetailsUpdated ? "active" : "disabled"}
								label={<span className={styles?.button_label}>{Constants.MAKE_CHANGES}</span>}
							/>
						)}
					</div>
					<div className={styles?.link_button_wrapper}>
						{readOnly && isGroupActive && createGroupAccess && (
							<Button className={styles?.edit_button} type="link" onClick={() => handleMode(ViewMode.EDIT)}>
								<div className="flex flex-row items-center">{Constants.EDIT_GROUP}</div>
								<div className="flex items-center justify-center ml-[5px]">{getIcon({ icon: "editLink" })}</div>
							</Button>
						)}
						{mode === ViewMode.EDIT && (
							<Button className={styles?.edit_button} type="link" onClick={() => handleGoBack()}>
								<div className="flex flex-row items-center">{Constants.GO_BACK}</div>
								<div className="flex items-center justify-center ml-[5px]">{getIcon({ icon: "editLink" })}</div>
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default EditGroupPage;
